import { useCallback, useRef, useState } from 'react';

/**
 * Draggable splitters between panes.
 *
 * Every screen here is a fixed sidebar beside something elastic, and the right
 * width depends on the day: a dispatcher reading long addresses wants the list
 * wider, one watching the map wants it gone. Rather than guess, let them drag
 * it, and remember what they chose.
 *
 * Sizes are px in React state and applied as inline grid tracks, not CSS
 * classes: a drag is a continuous value, and there is no sensible set of
 * classes for "313 pixels".
 */

export interface PaneSize {
  size: number;
  setSize: (next: number) => void;
  /** Hand straight to a Resizer. */
  onResize: (delta: number) => void;
}

/**
 * A remembered pane dimension.
 *
 * Clamped on the way in as well as during a drag, so a stored value from a
 * wider monitor cannot leave a pane off the screen on a laptop.
 */
export function usePaneSize(
  key: string,
  initial: number,
  bounds: { min: number; max: number },
): PaneSize {
  const clamp = useCallback(
    (value: number) => Math.min(bounds.max, Math.max(bounds.min, Math.round(value))),
    [bounds.min, bounds.max],
  );

  const [size, setSizeState] = useState(() => {
    const stored = Number(localStorage.getItem(key));
    return clamp(Number.isFinite(stored) && stored > 0 ? stored : initial);
  });

  const setSize = useCallback(
    (next: number) => {
      const value = clamp(next);
      localStorage.setItem(key, String(value));
      setSizeState(value);
    },
    [key, clamp],
  );

  /* Read through a ref: a drag emits deltas from where it started, not from
   * wherever the last render happened to leave the value. */
  const current = useRef(size);
  current.current = size;
  const onResize = useCallback((delta: number) => setSize(current.current + delta), [setSize]);

  return { size, setSize, onResize };
}

export function Resizer({
  orientation,
  onResize,
  label,
  onFit,
  /** Dragging right or down normally grows the pane; -1 when it shrinks it. */
  direction = 1,
}: {
  orientation: 'vertical' | 'horizontal';
  onResize: (delta: number) => void;
  label: string;
  /**
   * Size the pane to its contents. Double-click, which is where people already
   * try it -- it is how a column divider behaves in every spreadsheet.
   */
  onFit?: () => void;
  direction?: 1 | -1;
}) {
  /*
   * Whether a drag is running lives in a ref, not in state.
   *
   * State is asynchronous: a pointermove arriving in the same task as the
   * pointerdown reads the old value and the move is dropped. With a real mouse
   * that is usually hidden by the frame between the two events, which makes it
   * exactly the kind of bug that shows up only under a fast drag. The state
   * below exists purely so the handle can look active.
   */
  const active = useRef(false);
  const [dragging, setDragging] = useState(false);
  const origin = useRef(0);

  const vertical = orientation === 'vertical';

  return (
    <div
      className="resizer"
      data-orientation={orientation}
      data-dragging={dragging ? 'true' : undefined}
      role="separator"
      aria-orientation={vertical ? 'vertical' : 'horizontal'}
      aria-label={label}
      tabIndex={0}
      onPointerDown={(event) => {
        event.preventDefault();
        origin.current = vertical ? event.clientX : event.clientY;
        active.current = true;
        setDragging(true);
        /*
         * Capture on the handle so the drag survives the pointer outrunning it,
         * which it will: the pane resizes a frame behind the finger.
         */
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        if (!active.current) return;
        const position = vertical ? event.clientX : event.clientY;
        const delta = position - origin.current;
        if (delta === 0) return;
        origin.current = position;
        onResize(delta * direction);
      }}
      onPointerUp={(event) => {
        active.current = false;
        setDragging(false);
        event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      onPointerCancel={() => {
        active.current = false;
        setDragging(false);
      }}
      onDoubleClick={onFit}
      title={onFit ? `${label} — double-click to fit the contents` : label}
      onKeyDown={(event) => {
        // A splitter nobody can reach from the keyboard is a pane nobody can
        // resize without a mouse.
        const step = event.shiftKey ? 40 : 8;
        const back = vertical ? 'ArrowLeft' : 'ArrowUp';
        const forward = vertical ? 'ArrowRight' : 'ArrowDown';
        if (event.key === back) onResize(-step * direction);
        else if (event.key === forward) onResize(step * direction);
        else return;
        event.preventDefault();
      }}
    />
  );
}
